import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";

import { IMAGES, SVGICON } from "../../constant/theme";
import LogoutPage from "./Logout";
import Logoutbtn from "./Logoutbtn";

import { useSelector, useDispatch } from "react-redux";
import { setCurrentUser } from "../../../store/actions/UserActions";

const API_BASE = (process.env.REACT_APP_PREDICTIONS_API || "").replace(/\/+$/, "");

const safeParseUserDetails = () => {
  const raw = localStorage.getItem("userDetails");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed?.data || null;
  } catch (e) {
    return null;
  }
};

const buildAvatarSrc = (pic) => {
  const fallback = IMAGES?.Tab1 || "";

  if (!pic || typeof pic !== "string") return fallback;

  const trimmed = pic.trim();
  if (!trimmed) return fallback;

  if (trimmed.startsWith("data:image/")) return trimmed;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (API_BASE) {
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${API_BASE}${path}`;
  }

  return trimmed;
};

const UserMenu = ({ onNote }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);

  const [headerFix, setHeaderFix] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [userIsGroupAdmin, setUserIsGroupAdmin] = useState(false);

  const storedData = useMemo(() => safeParseUserDetails(), []);

  const avatarSrc = useMemo(() => buildAvatarSrc(currentUser?.pic), [currentUser?.pic]);

  const loadUserFromLocalStorage = useCallback(() => {
    if (!storedData) return;

    const updatedUser = {
      id: storedData.id,
      email: storedData.email,
      names: storedData.names,
      role: storedData.role,
      accessToken: storedData.accessToken,
      pic: storedData.pic,
    };

    dispatch(setCurrentUser(updatedUser));
  }, [dispatch, storedData]);

  useEffect(() => {
    const init = async () => {
      try {
        loadUserFromLocalStorage();
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [loadUserFromLocalStorage]);

  useEffect(() => {
    const handleScroll = () => setHeaderFix(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!storedData?.id || !API_BASE) return;

    const fetchUserGroups = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/getUserGroupsAdmin/${storedData.id}`);
        const groups = res?.data?.data?.userGroups || [];
        setUserIsGroupAdmin(groups.some((g) => g?.role === "groupAdmin"));
      } catch (error) {
        console.error("Error fetching user groups:", error);
        setUserIsGroupAdmin(false);
      }
    };

    fetchUserGroups();
  }, [storedData?.id]);

  const handleDropdownSelect = () => setDropdownOpen(false);
  const handleDropdownToggle = (isOpen) => setDropdownOpen(isOpen);

  const handleAvatarError = (e) => {
    if (e?.currentTarget?.src !== IMAGES?.Tab1) {
      e.currentTarget.src = IMAGES?.Tab1;
    }
  };

  if (loading) return <div>Loading...</div>;

  if (!currentUser) return <div>Error fetching user details.</div>;

  const showAdminOptions = currentUser.role !== "groupMember" || userIsGroupAdmin;

  return (
    <div className={`header ${headerFix ? "is-fixed" : ""}`}>
      <div
        className="header-content"
        style={{ background: "#01152F", border: "2px solid #01152F" }}
      >
        <nav className="navbar navbar-expand">
          <div className="collapse navbar-collapse justify-content-between">
            <div className="header-left">
              <h2 style={{ color: "#FFF" }}>
                <Link to="/dashboard" className="brand-logo" style={{ color: "#FFF" }}>
                  <span style={{ color: "#F1D34F" }}>NYE Predictions</span>
                </Link>
              </h2>
            </div>

            <ul className="navbar-nav header-right">
              <li className="nav-item align-items-center header-border">
                <Logoutbtn />
              </li>

              <li className="nav-item ps-3">
                <Dropdown
                  className="header-profile2"
                  onSelect={handleDropdownSelect}
                  onToggle={handleDropdownToggle}
                  show={dropdownOpen}
                >
                  <Dropdown.Toggle className="nav-link i-false" as="div">
                    <div className="header-info2 d-flex align-items-center">
                      <div className="header-media">
                        <img
                          src={avatarSrc}
                          alt="avatar"
                          style={{ height: "115%", width: "115%" }}
                          onError={handleAvatarError}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end">
                    <div className="card border-0 mb-0">
                      <div className="card-header py-2">
                        <div className="products">
                          <img
                            src={avatarSrc}
                            className="avatar avatar-md"
                            alt="avatar"
                            onError={handleAvatarError}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                          <div>
                            <h6>{currentUser.names}</h6>
                          </div>
                        </div>
                      </div>

                      <div className="card-body px-0 py-2" onClick={handleDropdownSelect}>
                        <Link to="/member-profile" className="dropdown-item ai-icon">
                          {SVGICON.User}
                          <span className="ms-2">Profile</span>
                        </Link>
                      </div>

                      {showAdminOptions ? (
                        <div className="card-body px-0 py-2" onClick={handleDropdownSelect}>
                          <Link to="/admin-access" className="dropdown-item ai-icon">
                            {SVGICON.User}
                            <span className="ms-2">Admin Options</span>
                          </Link>
                        </div>
                      ) : (
                        <div className="card-body px-0 py-2" onClick={handleDropdownSelect}>
                          <Link to="/admin-access-member" className="dropdown-item ai-icon">
                            {SVGICON.User}
                            <span className="ms-2">Create Group</span>
                          </Link>
                        </div>
                      )}

                      <div className="card-footer px-0 py-2">
                        <LogoutPage />
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default UserMenu;
